import { useCallback, useState } from 'preact/hooks';

/**
 * Parameters for configuring switch event handlers.
 * @typedef {Object} SwitchHandlersParams
 * @property {boolean} [isDisabled] - Whether the switch is disabled.
 * @property {boolean} [isReadonly] - Whether the switch is read-only.
 * @property {(event: MouseEvent | KeyboardEvent) => void} [onChange] - Callback fired on click or keyup (Space/Enter).
 */
type SwitchHandlersParams = {
  isDisabled?: boolean;
  isReadonly?: boolean;
  onChange?: (event: MouseEvent | KeyboardEvent) => void;
};

/**
 * Provides standardized event handlers for switch UI components (such as StyledSwitch).
 *
 * - Handles mouse click and keyboard activation (Space/Enter) on <kbd>keyup</kbd> (legacy behavior).
 * - Implements "gating" for Enter and Space: onChange will only trigger on keyup if that key's keydown
 *   happened on the component, preventing accidental activation when focus shifts mid-keypress.
 *   (e.g. Enter down elsewhere, focus moves to switch, Enter up: switch does NOT toggle)
 * - Ignores events if `isDisabled` or `isReadonly` are true.
 * - Internally uses isEnterPressed and isSpacePressed state to track legitimate activation origin.
 * - Forwards `onChange` when value should toggle.
 *
 * @param {SwitchHandlersParams} params - Props controlling switch behavior and callbacks.
 * @returns {{
 *   onClick: (e: MouseEvent) => void;
 *   onKeyDown: (e: KeyboardEvent) => void;
 *   onKeyUp: (e: KeyboardEvent) => void;
 * }} Event handlers to be spread on the root switch element.
 *
 * @example
 *   const { onClick, onKeyDown, onKeyUp } = useSwitchHandlers(...);
 *   <div tabIndex={0} onClick={onClick} onKeyDown={onKeyDown} onKeyUp={onKeyUp} ... />
 */
export function useSwitchHandlers({ isDisabled, isReadonly, onChange }: SwitchHandlersParams) {
  const [isEnterPressed, setIsEnterPressed] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (isDisabled || isReadonly) return;
      e.preventDefault();
      e.stopPropagation();
      onChange?.(e);
    },
    [isDisabled, isReadonly, onChange]
  );

  /**
   * Gating logic for Enter and Space:
   * Only "arms" activation on the *first* keydown (when event.repeat is false).
   * This means that if a key is held down elsewhere and focus moves to the switch
   * (e.g. user presses and holds Enter on a button, button sets focus to switch
   * on activation, then more auto-repeated keydowns will NOT mistakenly arm the switch),
   * only an Enter or Space press that originated with the switch actually focused
   * will allow a subsequent keyup to activate.
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isDisabled || isReadonly) return;
      if (e.code === 'Enter' && !e.repeat) {
        setIsEnterPressed(true);
        e.preventDefault();
      }
      if (e.code === 'Space' && !e.repeat) {
        setIsSpacePressed(true);
        e.preventDefault();
      }
    },
    [isDisabled, isReadonly]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (isDisabled || isReadonly) return;
      if (e.code === 'Enter' && isEnterPressed) {
        setIsEnterPressed(false);
        e.preventDefault();
        e.stopPropagation();
        onChange?.(e);
      }
      if (e.code === 'Space' && isSpacePressed) {
        setIsSpacePressed(false);
        e.preventDefault();
        e.stopPropagation();
        onChange?.(e);
      }
    },
    [isDisabled, isReadonly, isEnterPressed, isSpacePressed, onChange]
  );

  return {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    onKeyPress: handleKeyPress
  };
}

/**
 * Prevent default on Space otherwise holding down the Space will cause the page to scroll to the bottom.
 */
function handleKeyPress(e: KeyboardEvent) {
  if (e.code === 'Space') {
    e.preventDefault();
  }
}
